<template>
  <div>
    <div
      class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded"
      :class="[color === 'light' ? 'bg-white' : 'bg-emerald-900 text-white']"
    >
      <div class="rounded-t mb-0 px-4 py-3 border-0">
        <div class="text-center flex items-center justify-between">
          <h3
            class="font-semibold text-lg"
            :class="[color === 'light' ? 'text-blueGray-700' : 'text-white']"
            v-if="title"
          >
            {{ $t(title) }}
            <button
              @click="toggleFilter()"
              :title="$t(filterEnabled ? 'Cancelar filtros' : 'Filtrar datos')"
              v-if="!hideFilters"
            >
              <i
                :class="
                  'fa ml-2 ' +
                  (filterEnabled ? 'fa-filter-circle-xmark' : 'fa-filter')
                "
              ></i>
            </button>
          </h3>
          <div class="flex items-end gap-2">
            <slot name="actions"></slot>
            <router-link
              :to="{
                path: '/' + calculatedRecordResource + '/new',
                query: { defaults: $route.query.filters }
              }"
              v-if="!readonly"
              class="text-white bg-emerald-500 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ml-2 ease-linear transition-all duration-150"
            >
              {{ $t("button.new") }}
            </router-link>
          </div>
        </div>
      </div>
      <div class="block w-full overflow-x-auto">
        <table
          class="table-fixed items-center w-full bg-transparent"
          v-if="!loading"
        >
          <thead v-if="!hideHeaders">
            <tr v-if="groups">
              <th
                v-for="(group, groupName) in groups"
                :key="groupName"
                :colspan="group.colspan"
                class="px-6 align-middle py-3 text-sm uppercase font-semibold text-center"
                :class="[
                  groupName.startsWith('default')
                    ? ''
                    : color === 'light'
                    ? 'text-blueGray-500 border-blueGray-100'
                    : 'text-emerald-300 border-emerald-700',
                  groupName.startsWith('default') ? '' : 'border border-solid',
                ]"
                :style="
                  !groupName.startsWith('default') && group?.color
                    ? { backgroundColor: group.color }
                    : {}
                "
              >
                <template v-if="!groupName.startsWith('default')">
                  {{ $t("groups." + groupName) }}
                </template>
              </th>
            </tr>
            <tr>
              <th
                v-if="this.showSelector"
                class="px-6 align-middle border border-solid py-3 text-sm uppercase border-l-0 border-r-0 font-semibold text-center w-5"
                :class="[
                  color === 'light'
                    ? 'bg-blueGray-50 text-blueGray-500 border-blueGray-100'
                    : 'bg-emerald-800 text-emerald-300 border-emerald-700',
                ]"
              >
                <input
                  type="checkbox"
                  :checked="allRecordsSelected"
                  @input="toggleSelectAll($event.target.value)"
                  class="px-3 py-3 placeholder-blueGray-300 text-blueGray-600 rounded text-sm focus:outline-none focus:ring ease-linear transition-all duration-150 border-gray-300 bg-white shadow cursor-pointer"
                  :title="
                    $t(
                      'button.' +
                        (allRecordsSelected ? 'unselectAll' : 'selectAll'),
                    )
                  "
                />
              </th>
              <th
                v-if="!this.hideActions"
                class="px-6 align-middle border border-solid py-3 text-sm uppercase border-l-0 border-r-0 font-semibold text-left w-5"
                :class="[
                  color === 'light'
                    ? 'bg-blueGray-50 text-blueGray-500 border-blueGray-100'
                    : 'bg-emerald-800 text-emerald-300 border-emerald-700',
                ]"
              ></th>
              <th
                v-for="column in filterColumns"
                :key="column.name"
                class="px-6 align-middle border border-solid py-3 text-sm border-l-0 border-r-0 font-semibold text-left w-32"
                :class="[
                  color === 'light'
                    ? 'bg-blueGray-50 text-blueGray-500 border-blueGray-100'
                    : 'bg-emerald-800 text-emerald-300 border-emerald-700',
                ]"
                :style="column?.color ? { backgroundColor: column.color } : {}"
              >
                <button
                  @click="sortByColumn(column)"
                  v-if="sortEnabled && !column.doNotSort"
                  :title="
                    sortBy === column.name
                      ? sortTitle
                      : 'Click para ordenar por esta columna'
                  "
                >
                  {{ $t("fields." + (typeof column.label === 'function' ? column.label({}) : column.label)) }}
                  <i
                    class="fa ml-2"
                    :class="itIsSortedByColumn(column) ? sortIcon : 'fa-sort'"
                  ></i>
                </button>
                <template v-else>
                  {{ $t("fields." + (typeof column.label === 'function' ? column.label({}) : column.label)) }}
                </template>
              </th>
            </tr>
            <tr v-if="filterEnabled && !hideFilters">
              <th
                v-if="this.showSelector"
                class="px-6 align-middle border border-solid py-3 text-sm uppercase border-l-0 border-r-0 font-semibold text-left"
                :class="[
                  color === 'light'
                    ? 'bg-blueGray-50 text-blueGray-500 border-blueGray-100'
                    : 'bg-emerald-800 text-emerald-300 border-emerald-700',
                ]"
              ></th>
              <th
                v-if="!this.hideActions"
                class="px-6 align-middle border border-solid py-3 text-sm uppercase border-l-0 border-r-0 font-semibold text-left"
                :class="[
                  color === 'light'
                    ? 'bg-blueGray-50 text-blueGray-500 border-blueGray-100'
                    : 'bg-emerald-800 text-emerald-300 border-emerald-700',
                ]"
              ></th>
              <th
                v-for="column in filterColumns"
                :key="column.name"
                class="px-6 align-middle border border-solid py-3 text-sm border-l-0 border-r-0 font-semibold text-left"
                :class="[
                  color === 'light'
                    ? 'bg-blueGray-50 text-blueGray-500 border-blueGray-100'
                    : 'bg-emerald-800 text-emerald-300 border-emerald-700',
                ]"
              >
                <template
                  v-if="
                    (primaryKey === column.name && column.type === 'INTEGER') ||
                    column.doNotFilter
                  "
                >
                </template>
                <template
                  v-else-if="['STRING', 'LONGSTRING'].includes(column.type)"
                >
                  <input
                    type="text"
                    :value="filtersValues[column.name]"
                    @input="updateFilterValue(column, $event.target.value)"
                    class="min-w-50-px border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                  />
                </template>
                <template
                  v-else-if="
                    ['DATE', 'INTEGER', 'DECIMAL'].includes(column.type)
                  "
                >
                  <div class="flex items-center gap-1">
                    <!-- type DATE -->
                    <template v-if="column.type === 'DATE'">
                      <input
                        type="date"
                        :value="filtersValues[column.name]"
                        @blur="updateFilterValue(column, $event.target.value)"
                        class="min-w-50-px border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      />
                    </template>
                    <!-- type INTEGER or DECIMAL -->
                    <template
                      v-else-if="['INTEGER', 'DECIMAL'].includes(column.type)"
                    >
                      <input
                        type="number"
                        :step="
                          column.type === 'INTEGER'
                            ? '1'
                            : column.step || '0.25'
                        "
                        :value="filtersValues[column.name]"
                        @input="updateFilterValue(column, $event.target.value)"
                        :min="column.min"
                        :max="column.max"
                        class="min-w-50-px border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      />
                    </template>
                    <select-join
                      :column="operatorsColumn"
                      :record="filters[column.name] || {}"
                      @pickValue="updateFilterOperator(column, $event)"
                      class="min-w-50-px"
                    />
                  </div>
                </template>
                <template v-else-if="column.type === 'PASSWORD'">
                  <!-- nothing to do -->
                </template>
                <template v-else>
                  <select-join
                    :column="parseToFilter(column)"
                    :record="filtersValues"
                    @pickValue="updateFilterValue(column, $event)"
                    class="min-w-50-px"
                  />
                </template>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="record in records"
              :key="record[primaryKey]"
              @dblclick="openRecord(record)"
              class="hover:bg-yellow-200"
              :class="
                selectedRecordsList[record[primaryKey]] ? 'bg-yellow-100' : ''
              "
            >
              <td
                v-if="this.showSelector"
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm p-4 text-center w-5"
              >
                <input
                  type="checkbox"
                  :checked="selectedRecordsList[record[primaryKey]]"
                  @input="toggleSelectRecord(record, $event.target.value)"
                  class="px-3 py-3 placeholder-blueGray-300 text-blueGray-600 rounded text-sm focus:outline-none focus:ring ease-linear transition-all duration-150 border-gray-300 bg-white shadow cursor-pointer"
                  :title="
                    $t(
                      'button.' +
                        (selectedRecordsList[record[primaryKey]]
                          ? 'unselect'
                          : 'select'),
                    )
                  "
                />
              </td>
              <td
                v-if="!this.hideActions"
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm p-4 w-5"
              >
                <router-link
                  :to="recordLink(record)"
                  class="text-white bg-blueGray-500 font-bold uppercase text-sm px-2 py-2 shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 rounded"
                  title="Abrir registro"
                >
                  <i class="fas fa-arrow-right"></i>
                </router-link>
              </td>
              <td
                v-for="column in columnsToDisplay"
                :key="column.name"
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-sm p-4 w-32"
                :class="columnClass(column)"
              >
                <template
                  v-if="
                    [
                      'STRING',
                      'PASSWORD',
                      'DATE',
                      'INTEGER',
                      'DECIMAL',
                      'LONGSTRING',
                    ].includes(column.type)
                  "
                >
                  <span :title="getTooltip(column, record)">
                    {{ parseColumn(column, record) }}
                  </span>
                </template>
                <template v-else-if="column.type === 'BOOLEAN'">
                  <input
                    type="checkbox"
                    :checked="record[column.name]"
                    disabled="true"
                    class="bg-gray-100 border border-gray-300 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 rounded text-sm focus:outline-none focus:ring ease-linear transition-all duration-150"
                  />
                </template>
                <template v-else>
                  <select-join
                    :column="column"
                    :record="record"
                    disabled="true"
                    transparent="true"
                  />
                </template>
              </td>
            </tr>
            <tr v-if="records?.length === 0">
              <td
                :colspan="columns.length + 1"
                class="py-10 px-3 text-center text-blueGray-400"
              >
                <i class="fa fa-5x fa-folder-open"></i>
                <p class="py-3">
                  {{ $t("No hay datos para mostrar") }}
                </p>
              </td>
            </tr>
          </tbody>
          <slot name="tfoot"></slot>
        </table>
        <div v-else class="py-10 px-3 text-center text-4xl">
          <i class="fa fa-spin fa-spinner"></i>
        </div>
      </div>
    </div>

    <slot name="after-table"></slot>

    <div class="flex justify-between align-middle mt-8">
      <div class="flex align-middle">
        <template v-if="!unableSelect">
          <button
            @click="toggleCompare()"
            class="bg-blueGray-500 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
          >
            {{
              $t(
                "button." +
                  (showOnlySelectedRecords ? "showAllRecords" : "showSelected"),
              )
            }}
          </button>
        </template>
      </div>

      <card-pagination
        :totalPages="parseInt(meta.totalPages)"
        :page="parseInt(meta.page)"
        @changePage="handleChangePage"
        v-if="meta && !loading"
      />
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import SelectJoin from "@/components/Forms/SelectJoin.vue";
import CardPagination from "@/components/Cards/CardPagination.vue";
import { useToast } from "vue-toastification";

// Models
import { cargo } from "@/models/cargo";
import { contacto } from "@/models/contacto";
import { cuenta } from "@/models/cuenta";
import { directo_hora } from "@/models/directo_hora";
import { empresa } from "@/models/empresa";
import { feriado } from "@/models/feriado";
import { hora } from "@/models/hora";
import { licencia } from "@/models/licencia";
import { moneda } from "@/models/moneda";
import { obra } from "@/models/obra";
import { oficina } from "@/models/oficina";
import { pago_categoria } from "@/models/pago_categoria";
import { pago_subcategoria } from "@/models/pago_subcategoria";
import { personal } from "@/models/personal";
import { presupuesto } from "@/models/presupuesto";
import { rol } from "@/models/rol";
import { tarea } from "@/models/tarea";
import { transaccion } from "@/models/transaccion";
import { usuario } from "@/models/usuario";

export default {
  data() {
    return {
      filterEnabled: false,
      filters: {},
      filtersValues: {},
      filtersTimeOuts: {},
      sortBy: {},
      order: {},
      page: {},
      selectedRecordsList: {},
      operatorsColumn: {
        name: "operator",
        type: "STRINGINLIST",
        options: ["<", "<=", "==", ">=", ">"],
        allowNull: true,
        values: {},
      },
      dataTypes: {
        cargo: {
          model: cargo,
        },
        contacto: {
          model: contacto,
        },
        cuenta: {
          model: cuenta,
        },
        directo_hora: {
          model: directo_hora,
        },
        empresa: {
          model: empresa,
        },
        feriado: {
          model: feriado,
        },
        hora: {
          model: hora,
        },
        licencia: {
          model: licencia,
        },
        moneda: {
          model: moneda,
        },
        obra: {
          model: obra,
        },
        oficina: {
          model: oficina,
        },
        pago_categoria: {
          model: pago_categoria,
        },
        pago_subcategoria: {
          model: pago_subcategoria,
        },
        personal: {
          model: personal,
        },
        presupuesto: {
          model: presupuesto,
        },
        rol: {
          model: rol,
        },
        tarea: {
          model: tarea,
        },
        transaccion: {
          model: transaccion,
        },
        usuario: {
          model: usuario,
        },
      },
    };
  },
  components: {
    SelectJoin,
    CardPagination,
  },
  props: {
    title: {},
    resource: {},
    primaryKey: {},
    records: {
      default: null,
    },
    meta: {},
    columns: {},
    readonly: {},
    hideHeaders: {},
    hideActions: {},
    hideFilters: {},
    doNotSort: {},
    unableSelect: {},
    color: {
      default: "light",
      validator: function (value) {
        // The value must match one of these strings
        return ["light", "dark"].indexOf(value) !== -1;
      },
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler() {
        this.sortBy = this.$route.query.sortBy;
        this.order = this.$route.query.order;
        this.page = this.$route.query.page;
        this.filters = JSON.parse(this.$route.query.filters || "{}");
        this.filterEnabled = Object.keys(this.filters).length > 0;

        ["sortBy", "order", "page"].forEach((property) => {
          this.$watch(
            () => this[property],
            () => this.queryParamProcess(),
          );
        });
      },
    },
    filters: {
      immediate: true,
      handler() {
        this.filtersValues = Object.fromEntries(
          Object.entries(this.filters).map(([key, value]) => [
            key,
            value?.value,
          ]),
        );
      },
    },
  },

  computed: {
    ...mapState({
      userInfo: (state) => state.auth.user,
    }),

    roles() {
      return this.userInfo?.roles || [];
    },

    loading() {
      return !this.records;
    },

    sortIcon() {
      let icon;
      switch (this.order) {
        case "ASC":
          icon = "fa-sort-down";
          break;
        case "DESC":
          icon = "fa-sort-up";
          break;
        default:
          icon = "fa-sort";
          break;
      }
      return icon;
    },

    sortTitle() {
      let title;
      switch (this.order) {
        case "ASC":
          title = "Ascendente";
          break;
        case "DESC":
          title = "Descendente";
          break;
        default:
          title = "Ordenar por esta columna";
          break;
      }
      return title;
    },

    sortEnabled() {
      return !this.doNotSort;
    },

    navResource() {
      const path = this.$route.path;
      return path.startsWith("/") ? path.slice(1) : path;
    },

    calculatedRecordResource() {
      return this.resource ? this.resource : this.navResource;
    },

    columnsToDisplay() {
      return this.columns.filter((column) => this.checkRolesForColumn(column));
    },

    groups() {
      // Paleta de colores pasteles claros
      const pastelColors = [
        "#FFEBEE",
        "#FFF3E0",
        "#FFFDE7",
        "#E8F5E9",
        "#E3F2FD",
        "#F3E5F5",
        "#FCE4EC",
        "#E0F7FA",
        "#FFF8E1",
        "#F9FBE7",
        "#E1F5FE",
        "#F1F8E9",
        "#F8BBD0",
        "#D1C4E9",
        "#B3E5FC",
        "#DCEDC8",
        "#FFECB3",
        "#FFE0B2",
        "#D7CCC8",
        "#F5F5F5",
      ];

      let idx = 0;
      let groups = {};
      let defaultCount = 1;
      let lastWasDefault = false;
      let defaultExtraColSpan = 0;
      for (let extra of [this.showSelector, !this.hideActions]) {
        if (extra) {
          defaultExtraColSpan++;
        }
      }

      this.columnsToDisplay.forEach((column) => {
        let groupName;
        if (column.group) {
          groupName = column.group;
          lastWasDefault = false;
        } else {
          groupName = defaultCount === 1 ? "default" : `default${defaultCount}`;
          if (!lastWasDefault) {
            defaultCount++;
          }
          lastWasDefault = true;
        }
        groups[groupName] = groups[groupName] || {
          name: groupName,
          color: column.color || pastelColors[idx++ % pastelColors.length],
          colspan: groupName === "default" ? defaultExtraColSpan : 0,
        };

        groups[groupName].colspan = ++groups[groupName].colspan;
      });

      // Si solo tiene la key 'default', retorna null
      const keys = Object.keys(groups);
      if (keys.length === 1 && keys[0] === "default") {
        return null;
      }

      return groups;
    },

    filterColumns() {
      let groups = this.groups || {};

      // Asignar color pastel basado en el índice de la columna
      // filterAs permite filtrar por un campo distinto al que se muestra
      return this.columnsToDisplay.map((obj) => ({
        ...obj,
        ...(obj.filterAs || {}),
        allowNull: true,
        color: groups[obj.group]?.color || null,
      }));
    },

    showSelector() {
      return !this.unableSelect && !this.showOnlySelectedRecords;
    },

    allRecordsSelected() {
      let allRecordsSelected = true;
      if (!this.records?.length) {
        return false;
      }

      for (let record of this.records) {
        if (!this.selectedRecordsList[record[this.primaryKey]]) {
          allRecordsSelected = false;
          break;
        }
      }
      return allRecordsSelected;
    },

    showOnlySelectedRecords() {
      return this.filters[this.primaryKey]?.type === "IN";
    },
  },

  methods: {
    recordLink(record) {
      const primaryKey = this.primaryKey;
      return `/${this.calculatedRecordResource}/${record[primaryKey]}`;
    },

    openRecord(record) {
      this.$router.push(this.recordLink(record));
    },

    handleChangePage(page) {
      this.page = page;
    },

    sortByColumn(column) {
      const keyword = this.getSortKeyword(column);
      if (this.sortBy !== keyword) {
        this.sortBy = keyword;
        this.order = "ASC";
        this.page = 1;
      } else if (this.order === "ASC") {
        this.order = "DESC";
      } else {
        this.order = "ASC";
      }
    },

    toggleFilter() {
      this.filterEnabled = !this.filterEnabled;
      if (!this.filterEnabled) {
        this.filters = {};
        this.queryParamProcess();
      }
    },

    toggleSelectAll() {
      const newValue = !this.allRecordsSelected;
      for (let record of this.records) {
        const id = record[this.primaryKey];
        this.selectedRecordsList[id] = newValue;
      }
    },

    toggleSelectRecord(record) {
      const id = record[this.primaryKey];
      this.selectedRecordsList[id] = !this.selectedRecordsList[id];
      if (!this.selectedRecordsList[id]) {
        delete this.selectedRecordsList[id];
      }
    },

    itIsSortedByColumn(column) {
      return this.sortBy === this.getSortKeyword(column);
    },

    getSortKeyword(column) {
      if (
        ![
          "STRING",
          "LONGSTRING",
          "STRINGINLIST",
          "PASSWORD",
          "DATE",
          "INTEGER",
          "DECIMAL",
          "BOOLEAN",
        ].includes(column.type)
      ) {
        const model = this.dataTypes[column.type]?.model;
        const joinnedColumns = model?.filter((column) => column.showAsLabel);
        if (joinnedColumns.length > 0) {
          return `${column.as || column.type}.${joinnedColumns[0].name}`;
        }
      }
      return column.name;
    },

    updateFilterValue(column, value, operator) {
      const filter =
        !value && !operator ? undefined : this.filters[column.name] || {};

      if (filter) {
        filter.value = value || value == undefined ? value : filter.value;
        filter.type = column.type;

        switch (column.type) {
          case "STRING":
          case "LONGSTRING":
            filter.operator = "LIKE"; // 'LIKE'', '=='
            break;

          default:
            filter.operator = operator || filter.operator;
            break;
        }
      }

      this.filters[column.name] = filter;
      clearTimeout(this.filtersTimeOuts[column.name]);

      this.filtersTimeOuts[column.name] = setTimeout(() => {
        this.queryParamProcess();
      }, 300);
    },

    updateFilterOperator(column, operator) {
      const filter = !operator ? undefined : this.filters[column.name] || {};
      if (filter) {
        filter.operator = operator || filter.operator;
        this.updateFilterValue(column, filter.value, operator);
      }
    },

    queryParamProcess() {
      const queryParams = new URLSearchParams();
      let properties = ["sortBy", "order", "page", "filters"];

      for (let item of properties) {
        if (this[item]) {
          queryParams.set(
            item,
            item == "filters" ? JSON.stringify(this[item]) : this[item],
          );
        }
      }

      const queryString = queryParams.toString();
      const url = `/${this.navResource}?${queryString}`;
      this.$router.push(url);
    },

    checkRolesForColumn(column) {
      if (!column.roles) {
        return true;
      }

      let display = false;

      for (let rol of column.roles) {
        display = display || this.roles.includes(rol);
      }

      return display;
    },

    toggleCompare() {
      const showOnlySelectedRecords = !this.showOnlySelectedRecords;
      let filterValue;

      if (showOnlySelectedRecords) {
        filterValue = Object.keys(this.selectedRecordsList);

        if (!Object.keys(this.selectedRecordsList).length) {
          const toast = useToast();
          toast.warning(this.$t("error.selectAtLeastOneRecord"));
          return;
        }
      }

      this.page = 1;

      this.updateFilterValue(
        { name: this.primaryKey, type: "IN" },
        filterValue,
      );
    },

    columnClass(column) {
      let columnClass;
      if (column.currency) {
        columnClass = "text-right";
      } else if (column.type === "BOOLEAN") {
        columnClass = "text-center";
      } else {
        columnClass = "text-left";
      }

      if (["INTEGER", "DECIMAL"].includes(column.type)) {
        columnClass += " whitespace-nowrap";
      }

      return columnClass;
    },

    parseColumn(column, record, fullContent) {
      let value =
        typeof column.value === "function"
          ? column.value(record)
          : record[column.name];

      if (!value) {
        return value;
      }

      // `decimals` fuerza una cantidad fija de decimales en esa columna, por
      // encima del formato que le correspondería por su tipo
      if (column.decimals !== undefined) {
        value = new Intl.NumberFormat("es-UY", {
          minimumFractionDigits: column.decimals,
          maximumFractionDigits: column.decimals,
          useGrouping: true,
        }).format(value);
      } else if (column.currency || column.type === "INTEGER") {
        value = new Intl.NumberFormat("es-UY", {
          maximumFractionDigits: 0,
          useGrouping: true,
        }).format(value);
      } else if (column.type == "DECIMAL") {
        value = new Intl.NumberFormat("es-UY", {
          maximumFractionDigits: 2,
          useGrouping: true,
        }).format(value);
      } else if (column.type == "DATE") {
        const dateValue = new Date(value);
        if (isNaN(dateValue.getTime())) {
          return "—";
        }
        value = new Intl.DateTimeFormat("es-UY", {
          dateStyle: "short",
          timeZone: "UTC",
        }).format(dateValue);
        if (dateValue.getHours() !== 21 || dateValue.getMinutes() !== 0) {
          value += " ";
          value += new Intl.DateTimeFormat("es-UY", {
            timeZone: "UTC",
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
          }).format(dateValue);
        }
      } else if (
        ["STRING", "LONGSTRING"].includes(column.type) &&
        value.length > 32 &&
        !fullContent
      ) {
        value = value.slice(0, 32) + "...";
      }

      return value;
    },

    getTooltip(column, record) {
      let value = this.parseColumn(column, record, true);
      if (
        value &&
        ["STRING", "LONGSTRING"].includes(column.type) &&
        value.length > 32
      ) {
        return value;
      }

      return null;
    },

    parseToFilter(column) {
      const col = { ...column };

      delete col.multiple;
      delete col.filters;

      if (col.type === "BOOLEAN") {
        col.options = ["Sí", "No"];
      }

      return col;
    },
  },
};
</script>
